"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Link from "next/link";

const HEADLINE_LINES = [
  { text: "Vends tes affaires", colored: false },
  { text: "comme un pro.", colored: true },
];

/* Phone animation: cycles through bad listing -> scan -> good listing */
type PhonePhase = "bad" | "scanning" | "good" | "reset";

const PHASE_DURATIONS: Record<PhonePhase, number> = {
  bad: 2500,
  scanning: 2000,
  good: 3000,
  reset: 600,
};

const PHASE_ORDER: PhonePhase[] = ["bad", "scanning", "good", "reset"];

export default function HeroSection() {
  const [chars, setChars] = useState<
    { char: string; visible: boolean; colored: boolean }[]
  >([]);
  const [bodyVisible, setBodyVisible] = useState(false);
  const [ctaVisible, setCtaVisible] = useState(false);
  const [phonePhase, setPhonePhase] = useState<PhonePhase>("bad");
  const [scanProgress, setScanProgress] = useState(0);
  const badgeRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);

  // Headline char-by-char animation
  useEffect(() => {
    const allChars: {
      char: string;
      visible: boolean;
      colored: boolean;
      lineBreak: boolean;
    }[] = [];
    HEADLINE_LINES.forEach((line, lineIdx) => {
      for (const c of line.text) {
        allChars.push({ char: c, visible: false, colored: line.colored, lineBreak: false });
      }
      if (lineIdx < HEADLINE_LINES.length - 1) {
        allChars.push({ char: "\n", visible: false, colored: false, lineBreak: true });
      }
    });

    setChars(allChars);

    allChars.forEach((_, i) => {
      setTimeout(() => {
        setChars((prev) => prev.map((c, idx) => (idx === i ? { ...c, visible: true } : c)));
      }, 200 + i * 35);
    });

    const totalDelay = 200 + allChars.length * 35;
    setTimeout(() => setBodyVisible(true), totalDelay + 200);
    setTimeout(() => setCtaVisible(true), totalDelay + 500);
  }, []);

  // Phone phase cycling
  useEffect(() => {
    let phaseIdx = 0;
    let scanTimer: ReturnType<typeof setInterval> | null = null;

    function nextPhase() {
      phaseIdx = (phaseIdx + 1) % PHASE_ORDER.length;
      const phase = PHASE_ORDER[phaseIdx];
      setPhonePhase(phase);

      if (phase === "scanning") {
        setScanProgress(0);
        let progress = 0;
        scanTimer = setInterval(() => {
          progress += 0.02;
          if (progress >= 1) {
            progress = 1;
            if (scanTimer) clearInterval(scanTimer);
          }
          setScanProgress(progress);
        }, 40);
      }

      setTimeout(nextPhase, PHASE_DURATIONS[phase]);
    }

    const initial = setTimeout(nextPhase, PHASE_DURATIONS.bad);
    return () => {
      clearTimeout(initial);
      if (scanTimer) clearInterval(scanTimer);
    };
  }, []);

  // Parallax on scroll
  const handleScroll = useCallback(() => {
    const y = window.scrollY;
    if (phoneRef.current) phoneRef.current.style.transform = `translateY(${y * 0.08}px)`;
    if (badgeRef.current) badgeRef.current.style.transform = `translateY(${y * 0.15}px)`;
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const isGood = phonePhase === "good";
  const isScanning = phonePhase === "scanning";

  return (
    <section
      className="hero-section"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        padding: "120px 5vw 40px",
        gap: "4vw",
        position: "relative",
      }}
    >
      {/* Left column */}
      <div style={{ flex: "0 0 55%" }}>
        <h1
          style={{
            fontFamily: "var(--font-newsreader), Georgia, serif",
            fontStyle: "italic",
            fontSize: "clamp(48px, 6.5vw, 88px)",
            lineHeight: 0.95,
            fontWeight: 400,
            marginBottom: 28,
          }}
        >
          {chars.map((c, i) =>
            c.char === "\n" ? (
              <br key={i} />
            ) : (
              <span
                key={i}
                style={{
                  display: "inline-block",
                  opacity: c.visible ? 1 : 0,
                  transform: c.visible ? "translateY(0)" : "translateY(40px)",
                  transition: "all 0.5s cubic-bezier(0.22, 1, 0.36, 1)",
                  color: c.colored ? "#D4613A" : undefined,
                }}
              >
                {c.char === " " ? "\u00A0" : c.char}
              </span>
            )
          )}
        </h1>

        <p
          style={{
            fontSize: 17,
            lineHeight: 1.7,
            color: "rgba(44,37,32,0.65)",
            maxWidth: 480,
            marginBottom: 36,
            opacity: bodyVisible ? 1 : 0,
            transform: bodyVisible ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.7s ease",
          }}
        >
          Une photo depuis ton canap{"\u00E9"}, Vitrine g{"\u00E8"}re tout{"\u00A0"}:
          rendu studio professionnel, titre accrocheur, description
          compl{"\u00E8"}te, hashtags et prix juste. Pr{"\u00EA"}t {"\u00E0"} publier
          sur les plateformes de ton choix.
        </p>

        <div
          style={{
            opacity: ctaVisible ? 1 : 0,
            transform: ctaVisible ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.5s ease",
          }}
        >
          <Link
            href="/login"
            className="mkt-cta-btn"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "#D4613A",
              color: "#FDF6EE",
              padding: "16px 36px",
              borderRadius: 99,
              fontSize: 16,
              fontWeight: 600,
              textDecoration: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            Essayer gratuitement
          </Link>
          <span
            style={{
              display: "inline-block",
              marginLeft: 16,
              fontSize: 13,
              color: "rgba(44,37,32,0.45)",
              fontWeight: 500,
              opacity: ctaVisible ? 1 : 0,
              transition: "opacity 0.5s ease 0.3s",
            }}
          >
            Gratuit pendant la beta
          </span>
        </div>
      </div>

      {/* Right column - Animated Phone */}
      <div
        style={{
          flex: "0 0 40%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
        }}
      >
        {/* Glow - intensifies during scanning */}
        <div
          style={{
            position: "absolute",
            width: 400,
            height: 400,
            background: isScanning
              ? "radial-gradient(ellipse, rgba(212,97,58,0.15) 0%, transparent 70%)"
              : "radial-gradient(ellipse, rgba(212,97,58,0.06) 0%, transparent 70%)",
            borderRadius: "50%",
            zIndex: -1,
            transition: "background 0.8s ease",
          }}
        />

        {/* Phone */}
        <div
          ref={phoneRef}
          style={{
            width: 280,
            height: 580,
            background: "#000",
            borderRadius: 44,
            padding: 4,
            position: "relative",
            boxShadow: isScanning
              ? "0 30px 80px rgba(212,97,58,0.2), 0 4px 20px rgba(0,0,0,0.12), inset 0 0 0 1px rgba(255,255,255,0.08)"
              : "0 30px 80px rgba(44,37,32,0.18), 0 4px 20px rgba(0,0,0,0.1), inset 0 0 0 1px rgba(255,255,255,0.06)",
            willChange: "transform",
            transition: "box-shadow 0.6s ease",
          }}
        >
          {/* Inner screen */}
          <div
            style={{
              width: "100%",
              height: "100%",
              background: "#fff",
              borderRadius: 40,
              overflow: "hidden",
              position: "relative",
            }}
          >
          {/* Dynamic Island */}
          <div
            style={{
              position: "absolute",
              top: 10,
              left: "50%",
              transform: "translateX(-50%)",
              width: 100,
              height: 28,
              background: "#000",
              borderRadius: 20,
              zIndex: 10,
            }}
          />

          {/* Scan line */}
          {isScanning && (
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                height: 2,
                background: "#D4613A",
                boxShadow: "0 0 20px rgba(212,97,58,0.6), 0 0 60px rgba(212,97,58,0.3)",
                top: `${30 + scanProgress * 460}px`,
                zIndex: 8,
                transition: "top 0.04s linear",
              }}
            />
          )}

          {/* Phone content */}
          <div
            style={{
              width: "100%",
              height: "100%",
              padding: "36px 16px 16px",
              display: "flex",
              flexDirection: "column",
              gap: 6,
              fontSize: 12,
            }}
          >
            {/* User row */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0 8px" }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #b8a99a, #8c7b6b)",
                }}
              />
              <span style={{ fontSize: 11, fontWeight: 600, color: "#2C2520" }}>
                marie_vide_son_placard
              </span>
            </div>

            {/* Photo area - transitions between bad and good */}
            <div style={{ position: "relative", borderRadius: 8, overflow: "hidden" }}>
              {/* Bad photo - full casual snapshot */}
              <div
                style={{
                  height: 180,
                  overflow: "hidden",
                  borderRadius: 8,
                  opacity: isGood ? 0 : 1,
                  transform: isGood ? "scale(0.95)" : "scale(1)",
                  transition: "all 0.6s ease",
                  position: isGood ? "absolute" : "relative",
                  inset: isGood ? 0 : undefined,
                  zIndex: isGood ? 0 : 1,
                }}
              >
                <img
                  src="/marketing/dress-bad.webp"
                  alt=""
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "top",
                  }}
                />
              </div>

              {/* Good state: hero image + 4 variant thumbnails (matches real app) */}
              <div
                style={{
                  opacity: isGood ? 1 : 0,
                  transform: isGood ? "translateY(0)" : "translateY(10px)",
                  transition: "all 0.6s ease",
                  position: isGood ? "relative" : "absolute",
                  inset: isGood ? undefined : 0,
                  zIndex: isGood ? 1 : 0,
                }}
              >
                {/* Main hero image */}
                <div style={{ height: 180, borderRadius: 8, overflow: "hidden", position: "relative" }}>
                  <img
                    src="/marketing/dress-good-1.webp"
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "top",
                      transform: isGood ? "scale(1)" : "scale(1.05)",
                      transition: "transform 0.8s cubic-bezier(0.22, 1, 0.36, 1)",
                    }}
                  />
                  {/* Variant label badge */}
                  <div
                    style={{
                      position: "absolute",
                      top: 6,
                      left: 6,
                      background: "rgba(0,0,0,0.5)",
                      backdropFilter: "blur(8px)",
                      borderRadius: 99,
                      padding: "2px 8px",
                      fontSize: 7,
                      fontWeight: 700,
                      color: "#fff",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      opacity: isGood ? 1 : 0,
                      transition: "opacity 0.4s ease 0.3s",
                    }}
                  >
                    Studio
                  </div>
                </div>

                {/* 4 variant thumbnails strip */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 4,
                    marginTop: 4,
                  }}
                >
                  {[
                    { n: 1, label: "Studio" },
                    { n: 2, label: "Stylis\u00E9" },
                    { n: 3, label: "R\u00E9el" },
                    { n: 4, label: "D\u00E9tail" },
                  ].map((v, i) => (
                    <div
                      key={v.n}
                      style={{
                        borderRadius: 6,
                        overflow: "hidden",
                        border: i === 0 ? "1.5px solid #D4613A" : "1.5px solid rgba(44,37,32,0.08)",
                        opacity: isGood ? 1 : 0,
                        transform: isGood ? "translateY(0) scale(1)" : "translateY(8px) scale(0.9)",
                        transition: `all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${0.2 + i * 0.08}s`,
                      }}
                    >
                      <img
                        src={`/marketing/dress-good-${v.n}.webp`}
                        alt=""
                        style={{
                          width: "100%",
                          aspectRatio: "1",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Title - transitions */}
            <div
              style={{
                fontWeight: 600,
                marginTop: 6,
                color: "#2C2520",
                fontSize: 13,
                transition: "all 0.4s ease",
              }}
            >
              {isGood ? "Robe Longue Sézane Vert Sauge T36" : "robe verte taille 36"}
            </div>

            {/* Description */}
            <div
              style={{
                color: "rgba(44,37,32,0.5)",
                fontSize: 11,
                lineHeight: 1.4,
                transition: "all 0.4s ease",
                minHeight: 30,
              }}
            >
              {isGood
                ? "Robe longue fluide, coupe \u00E9vas\u00E9e. Port\u00E9e une fois pour un mariage."
                : `port\u00E9e 1 fois bon \u00E9tat`}
            </div>

            {/* Tags - only show in good state */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                marginTop: 4,
                minHeight: 22,
              }}
            >
              {isGood &&
                ["#robe", "#sezane", "#mariage", "#vert"].map((tag, i) => (
                  <span
                    key={tag}
                    style={{
                      background: "#FDF6EE",
                      border: "1px solid #E8DDD1",
                      padding: "2px 8px",
                      borderRadius: 99,
                      fontSize: 9,
                      fontWeight: 500,
                      opacity: isGood ? 1 : 0,
                      transform: isGood ? "scale(1)" : "scale(0.5)",
                      transition: `all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) ${0.2 + i * 0.08}s`,
                    }}
                  >
                    {tag}
                  </span>
                ))}
            </div>

            {/* Price */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: "auto",
                paddingBottom: 4,
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  fontSize: 18,
                  color: "#2C2520",
                  transition: "all 0.4s ease",
                }}
              >
                {isGood ? "65 EUR" : "20 EUR"}
              </span>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 12,
                  color: "rgba(44,37,32,0.4)",
                }}
              >
                <span style={{ color: "#e74c3c" }}>{"\u2665"}</span>
                {isGood ? " 24" : " 2"}
              </span>
            </div>
          </div>
          </div>{/* close inner screen */}
        </div>

        {/* Floating badge - animates with phone */}
        <div
          ref={badgeRef}
          style={{
            position: "absolute",
            top: 60,
            right: -30,
            background: "white",
            borderRadius: 12,
            padding: "8px 14px",
            fontSize: 13,
            fontWeight: 600,
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            border: "1px solid rgba(44,37,32,0.04)",
            display: "flex",
            alignItems: "center",
            gap: 6,
            willChange: "transform",
            zIndex: 5,
            transition: "all 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          <span style={{ color: "#e74c3c" }}>{"\u2665"}</span>
          {isGood ? "24 J\u2019aime" : "2 J\u2019aime"}
        </div>

        {/* Second floating badge - appears during good phase */}
        <div
          style={{
            position: "absolute",
            bottom: 80,
            left: -20,
            background: "white",
            borderRadius: 12,
            padding: "8px 14px",
            fontSize: 12,
            fontWeight: 600,
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            border: "1px solid rgba(44,37,32,0.04)",
            display: "flex",
            alignItems: "center",
            gap: 6,
            zIndex: 5,
            opacity: isGood ? 1 : 0,
            transform: isGood ? "translateY(0) scale(1)" : "translateY(10px) scale(0.9)",
            transition: "all 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.3s",
            color: "#3A7D5C",
          }}
        >
          <span style={{ fontSize: 14 }}>+200%</span> de vues
        </div>
      </div>

      {/* Mobile responsive overrides */}
      <style>{`
        @media (max-width: 768px) {
          .hero-section {
            flex-direction: column !important;
            padding: 100px 20px 60px !important;
            text-align: center !important;
          }
          .hero-section > div:first-child {
            flex: none !important;
            width: 100% !important;
          }
          .hero-section > div:last-child {
            flex: none !important;
            width: 100% !important;
            margin-top: 40px !important;
          }
          .hero-section > div:first-child p {
            margin-left: auto !important;
            margin-right: auto !important;
            margin-bottom: 32px !important;
          }
          .hero-section > div:last-child > div:nth-child(2) {
            width: 220px !important;
            height: 440px !important;
          }
        }
      `}</style>
    </section>
  );
}
